// M2 contracts: people and hosts (module 03), visits (module 04), visitor policies and approvals (module 07).
import { z } from 'zod';
import type { PassParkingDto } from './vehicles.ts';
import { ProfileFieldValues, ScreeningCategoryRuleSchema } from './people.ts';
import { CategoryWorkflowSchema, RestrictedAreaSchema } from './visitor-workflows.ts'; // ADR-027
import { RecurrenceSchema, type RoomWarningDto, type SkippedOccurrenceDto } from './recurrence.ts'; // PB-04-02, PB-12-03
import type { DeviceJobView } from './adapters.ts';
import type { NoticeReceiptDto, NoticeSigningOption } from './portal.ts'; // portal stream (PD-510, PD-511)

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time');
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');

// ------------------------------------------------------------------ visitor policy documents (PB-07-*)
export const REQUIRED_FIELD_VALUES = ['purpose', 'destination', 'visitor.email', 'visitor.phone', 'visitor.organisation'] as const;
const Label = z.object({ en: trimmed(80), ar: z.string().trim().max(80, 'v.tooLong').optional() });
const Resolver = z.discriminatedUnion('kind', [z.object({ kind: z.literal('site_approvers') }), z.object({ kind: z.literal('user'), userId: Uuid }), z.object({ kind: z.literal('group'), groupKey: Key })]);
const Stage = z.object({
  key: Key,
  label: Label,
  resolver: Resolver,
  dependsOn: z.array(Key).max(10).default([]),
  // PD-049/PD-051: n-of-m quorum, escalation and expiry timers (hours while waiting for a decision).
  quorum: z.number().int().min(1).max(10).optional(),
  escalateAfterHours: z.number().int().min(1).max(720).optional(),
  expiresAfterHours: z.number().int().min(1).max(2160).optional(),
});
// PD-045: which channels each trigger uses; omitted values use the documented defaults.
// 'delivery_notice': PB-09-14 (messaging stream, PD-528).
export const NOTIFICATION_TRIGGER_VALUES = ['invitation', 'reminder', 'cancellation', 'host_arrival', 'rsvp_reply', 'approval_requested', 'approval_decided', 'protocol_notice', 'delivery_notice'] as const;
export const CHANNEL_VALUES = ['email', 'sms', 'whatsapp'] as const;
const TriggerRule = z.object({ enabled: z.boolean().optional(), channels: z.array(z.enum(CHANNEL_VALUES)).max(3).optional() });
const NotificationOverrides = z.object({
  fallbackLocale: z.enum(['en', 'ar']).nullable().optional(),
  reminderHours: z.number().int().min(1).max(168).optional(),
  triggers: z.partialRecord(z.enum(NOTIFICATION_TRIGGER_VALUES), TriggerRule).optional(),
});
export const PolicyDocumentSchema = z.object({
  schema: z.literal(1),
  maxOccurrences: z.number().int().min(1).max(104),
  categories: z
    .array(
      z.object({
        code: Key,
        label: Label,
        active: z.boolean(),
        requiredFields: z.array(z.enum(REQUIRED_FIELD_VALUES)).max(REQUIRED_FIELD_VALUES.length),
        maxDays: z.number().int().min(1).max(366),
        stages: z.array(Stage).max(10),
        // PD-035: visitor pre-registration requirements for the category.
        preRegistration: z
          .object({
            confirmContact: z.boolean().optional(),
            editableContactFields: z.array(z.enum(['email', 'phone', 'organisation', 'name'])).max(4).optional(),
            notices: z.array(Key).max(10).optional(),
            // PD-450: validUntilRequired = the visitor states the document's validity date on upload.
            documents: z.array(z.object({ key: Key, label: Label, required: z.boolean(), validUntilRequired: z.boolean().optional() })).max(10).optional(),
            requiredBeforeArrival: z.boolean().optional(),
          })
          .optional(),
        notifications: NotificationOverrides.omit({ fallbackLocale: true }).optional(),
        // PB-03-06 (PD-295): watchlist screening at planning and arrival for this category.
        screening: ScreeningCategoryRuleSchema.optional(),
        // PD-052/PD-053: escort, visibility and kiosk use are configuration, never label-based code.
        escort: z.object({ required: z.boolean() }).optional(),
        visibility: z.enum(['standard', 'restricted']).optional(),
        kioskSelfService: z.boolean().optional(),
        purposeMode: z.enum(['free', 'list', 'list_or_other']).optional(),
        // PD-190 (module 08): identity verification the category requires.
        identity: z
          .object({
            requirement: z.enum(['none', 'warn', 'required']).optional(),
            acceptedMethods: z.array(z.enum(['uae_pass', 'emirates_id_reader', 'passport_mrz', 'document_ocr', 'manual'])).min(1).max(5).optional(),
            requiredProofs: z.array(z.enum(['identity', 'nationality', 'work_authorisation'])).min(1).max(3).optional(),
          })
          .optional(),
        // PD-350 (ADR-027): protocol, candidate, work-order and delivery behaviour of the visitor type.
        workflow: CategoryWorkflowSchema.optional(),
        // PB-02-03 (PD-069): credential profile of the type; narrows the site's credential rules only.
        credentialProfile: z
          .object({
            issue: z.enum(['allowed', 'not_allowed']).optional(),
            tokenTypes: z.array(z.enum(['card', 'pin', 'qr', 'mobile'])).min(1).max(4).optional(),
            includeEnclosingZones: z.boolean().optional(),
            includeCommonZones: z.boolean().optional(),
          })
          .optional(),
      }),
    )
    .min(1)
    .max(30),
  restrictedStages: z.array(Stage).max(10),
  notifications: NotificationOverrides.optional(),
  // PD-030: reception behaviour; omitted values use the documented defaults.
  reception: z
    .object({
      earlyArrivalMinutes: z.number().int().min(0).max(1440).optional(),
      allowReentryAfterCheckout: z.boolean().optional(),
      overstayGraceMinutes: z.number().int().min(0).max(1440).optional(),
      walkIns: z.object({ enabled: z.boolean().optional(), requireHostConfirmation: z.boolean().optional() }).optional(),
      returningEvidenceSoonDays: z.number().int().min(0).max(365).optional(), // PB-06-04 (PD-412)
    })
    .optional(),
  // PB-12-08 (PD-058): what of a person known only at other sites is reused here; omitted = identity.
  crossSiteReuse: z.enum(['off', 'identity', 'identity_and_organisation']).optional(),
  // PD-364 (ADR-027, PB-07-14): evidence and escort a restricted zone needs in a credential scope.
  restrictedArea: RestrictedAreaSchema.optional(),
});
export type PolicyDocumentInput = z.infer<typeof PolicyDocumentSchema>;

export const SavePolicyDraftRequest = z.object({
  siteId: Uuid.nullable(),
  /** 0 when creating a new draft. */
  expectedVersion: z.number().int().min(0),
  document: PolicyDocumentSchema,
  note: optionalText(500),
});
export const PublishPolicyRequest = z.object({ expectedVersion: z.number().int().min(1) });
export const RollbackPolicyRequest = z.object({ note: optionalText(500) });

export interface PolicyVersionResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded';
  readonly document: PolicyDocumentInput;
  readonly note: string | null;
  readonly basedOnId: string | null;
  readonly createdAt: string;
  readonly publishedAt: string | null;
  readonly version: number;
}
export interface PolicyOverviewResponse {
  readonly siteId: string | null;
  /** The version in force for this scope (site override or the shared organisation policy). */
  readonly effective: PolicyVersionResponse | null;
  readonly effectiveSource: 'site' | 'organisation' | 'none';
  readonly draft: PolicyVersionResponse | null;
  readonly history: readonly PolicyVersionResponse[];
}

/** Category list for planning forms; labels already localised. */
export interface VisitCategoryOption {
  readonly code: string;
  readonly label: string;
  readonly requiredFields: readonly string[];
  readonly maxDays: number;
  readonly approvalStages: number;
  /** OR-10: free text, list only, or list or other. */
  readonly purposeMode: 'free' | 'list' | 'list_or_other';
}

// ------------------------------------------------------------------ hosts and people (module 03)
export interface HostResponse {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly employeeNumber: string | null;
  readonly department: string | null;
  /** PB-18-07 (PD-541): the department reference; `department` is its name. */
  readonly departmentId: string | null;
  readonly jobTitle: string | null;
  readonly officeSiteId: string | null;
  readonly userId: string | null;
  readonly source: 'manual' | 'self' | 'directory' | 'import' | 'hr';
  readonly status: 'active' | 'inactive';
  readonly version: number;
  /** PB-09-14 (PD-530): mobile number for staff SMS notices; host managers only (null for others). */
  readonly mobilePhone: string | null;
}
export const CreateHostRequest = z.object({
  displayName: trimmed(200),
  email: z.email('v.email').max(254, 'v.tooLong').nullable().optional(),
  employeeNumber: z.string().trim().regex(/^[A-Za-z0-9._-]{1,50}$/, 'v.employeeNumber').nullable().optional(),
  department: optionalText(120),
  // PB-18-07 (PD-541): choose a department reference; when given it wins over the free text.
  departmentId: Uuid.nullable().optional(),
  jobTitle: optionalText(120),
  officeSiteId: Uuid.nullable().optional(),
  userId: Uuid.nullable().optional(),
  // PB-09-14 (PD-530): international format, e.g. +971501234567 (spaces and dashes are removed).
  mobilePhone: z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v.replace(/[\s-]/g, '')))
    .pipe(z.string().regex(/^\+[1-9][0-9]{6,14}$/, 'msg.v.mobilePhone').nullable())
    .nullable()
    .optional(),
});
export const UpdateHostRequest = CreateHostRequest.partial().extend({ expectedVersion: z.number().int().min(1), status: z.enum(['active', 'inactive']).optional() });

export interface PersonResponse {
  readonly id: string;
  readonly givenName: string;
  readonly familyName: string | null;
  /** Masked unless the viewer has an operational need (PB-03-07). */
  readonly email: string | null;
  readonly phone: string | null;
  readonly masked: boolean;
  readonly organisation: { readonly id: string; readonly name: string } | null;
  readonly version: number;
}
export interface PersonMatch {
  readonly id: string;
  readonly displayName: string;
  readonly organisation: string | null;
  readonly maskedEmail: string | null;
  readonly maskedPhone: string | null;
  readonly reasons: ReadonlyArray<'email' | 'phone' | 'name'>;
}
export const PersonMatchQuery = z.object({
  siteId: Uuid,
  email: z.string().trim().max(254).optional(),
  phone: z.string().trim().max(24).optional(),
  givenName: z.string().trim().max(100).optional(),
  familyName: z.string().trim().max(100).optional(),
});
export interface PersonHistoryResponse {
  readonly person: PersonResponse;
  readonly visits: ReadonlyArray<{
    readonly visitId: string;
    readonly reference: string;
    readonly siteId: string;
    readonly title: string | null;
    readonly startsAt: string | null;
    readonly planningStatus: string;
    readonly approvalStatus: string;
    readonly presenceStatus: string;
  }>;
  /** True when visits at sites outside the viewer's scope were withheld (PB-03-05). */
  readonly partialScope: boolean;
  /** PB-03-01 (PD-071): which source last supplied each core field, and when; never the values. */
  readonly provenance: Partial<Record<'givenName' | 'familyName' | 'email' | 'phone', { readonly source: string; readonly at: string }>>;
}

// ------------------------------------------------------------------ visits (module 04)
export const ScheduleSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('continuous'), start: z.object({ date: LocalDate, time: LocalTime }), end: z.object({ date: LocalDate, time: LocalTime }) }),
  z.object({ mode: z.literal('daily'), startDate: LocalDate, endDate: LocalDate, dailyStart: LocalTime, dailyEnd: LocalTime }),
]);
export type ScheduleDto = z.infer<typeof ScheduleSchema>;

export const ParticipantInput = z.object({
  /** Existing person chosen from match suggestions; otherwise a new profile is created. */
  personId: Uuid.optional(),
  givenName: z.string().trim().max(100, 'v.tooLong').optional(),
  familyName: optionalText(100),
  email: z
    .string()
    .trim()
    .max(254, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('v.email').nullable())
    .nullable()
    .optional(),
  phone: z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.string().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').nullable())
    .nullable()
    .optional(),
  organisation: optionalText(200),
  // OR-07 (PD-297): values of configured profile fields collected for the visit's category.
  fields: ProfileFieldValues.optional(),
});
export type ParticipantInput = z.infer<typeof ParticipantInput>;

const VisitFields = {
  title: optionalText(200),
  purpose: optionalText(500),
  /** OR-10: a value from the organisation's purpose list (the text becomes its label). */
  purposeCode: Key.nullable().optional(),
  categoryCode: Key.nullable().optional(),
  destinationId: Uuid.nullable().optional(),
  schedule: ScheduleSchema.nullable().optional(),
  /** Explicit choice when a local time occurs twice at a DST change. */
  disambiguation: z.enum(['earlier', 'later']).optional(),
  additionalHostIds: z.array(Uuid).max(10).optional(),
};

export const CreateVisitRequest = z.object({
  siteId: Uuid,
  ...VisitFields,
  /** Plan on behalf of another host (needs the delegated-host role, PD-024). Defaults to the caller. */
  primaryHostId: Uuid.optional(),
  participants: z.array(ParticipantInput).max(200).default([]),
  submit: z.boolean().default(false),
  /** PB-04-02: weekly or monthly, ending on a date or after a count (PD-470, PD-471). */
  recurrence: RecurrenceSchema.optional(),
  /** PB-12-03 (PD-477): the user acknowledged an over-capacity warning of a 'warn' destination. */
  acknowledgeRoomWarning: z.boolean().optional(),
});
export type CreateVisitRequest = z.infer<typeof CreateVisitRequest>;

export const UpdateVisitRequest = z.object({
  expectedVersion: z.number().int().min(1),
  ...VisitFields,
  primaryHostId: Uuid.optional(),
  /** Required when a submitted visit changes materially: confirms approvals will be re-requested. */
  confirmImpact: z.boolean().optional(),
  acknowledgeRoomWarning: z.boolean().optional(), // PB-12-03 (PD-477)
});
export type UpdateVisitRequest = z.infer<typeof UpdateVisitRequest>;

export const SubmitVisitRequest = z.object({ expectedVersion: z.number().int().min(1), disambiguation: z.enum(['earlier', 'later']).optional(), acknowledgeRoomWarning: z.boolean().optional() });
export const CancelVisitRequest = z.object({ expectedVersion: z.number().int().min(1), reason: trimmed(500) });
export const CopyVisitRequest = z.object({ schedule: ScheduleSchema.nullable().optional() });
export const AddParticipantsRequest = z.object({ expectedVersion: z.number().int().min(1), participants: z.array(ParticipantInput).min(1).max(200), acknowledgeRoomWarning: z.boolean().optional() });
export const RemoveParticipantRequest = z.object({ expectedVersion: z.number().int().min(1), reason: trimmed(500) });
export const AddNoteRequest = z.object({ body: trimmed(2000), correctsNoteId: Uuid.optional() });

export const VisitListQuery = z.object({
  siteId: Uuid,
  view: z.enum(['upcoming', 'drafts', 'past', 'cancelled', 'all']).default('upcoming'),
  scope: z.enum(['mine', 'site']).default('mine'),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  after: z.string().max(200).optional(),
});

export interface ParticipationResponse {
  readonly id: string;
  readonly person: PersonResponse;
  readonly organisationName: string | null;
  /** Independent facts (states.ts) — never collapsed into one status. */
  readonly approvalStatus: string;
  readonly evidenceStatus: string;
  readonly presenceStatus: string;
  readonly credentialStatus: string;
  readonly removed: boolean;
  readonly version: number;
  /** OR-03: attendance confirmation, independent of approval and presence. */
  readonly rsvpStatus: 'none' | 'confirmed' | 'declined';
  /** Latest invitation link and message for this person (host/delegate/reception views only). */
  readonly invitation: InvitationSummary | null;
  readonly lastMessage: MessageSummary | null;
  /** PB-05-07: pre-arrival readiness (null for viewers without an operational need). */
  readonly preRegistration: ParticipantReadiness | null;
}

export interface ApprovalTaskSummary {
  readonly id: string;
  readonly participationId: string;
  readonly stageKey: string;
  readonly stageLabel: string;
  readonly stageOrder: number;
  readonly dependsOn: readonly string[];
  readonly status: 'waiting' | 'pending' | 'blocked' | 'approved' | 'rejected' | 'invalidated' | 'closed';
  readonly resolver: { readonly kind: 'site_approvers' } | { readonly kind: 'user'; readonly userId: string; readonly displayName: string | null } | { readonly kind: 'group'; readonly groupKey: string; readonly label: string | null };
  readonly decidedBy: { readonly id: string; readonly displayName: string } | null;
  /** Set when the decider acted under a delegation (PB-07-12). */
  readonly onBehalfOf: { readonly id: string; readonly displayName: string } | null;
  readonly quorum: number;
  readonly votes: { readonly approve: number; readonly reject: number };
  readonly escalated: boolean;
  readonly decidedAt: string | null;
  readonly reason: string | null;
  readonly closedReason: string | null;
  readonly policyVersion: number;
  readonly visitVersion: number;
}

export interface VisitNoteResponse {
  readonly id: string;
  readonly body: string;
  readonly author: { readonly id: string; readonly displayName: string };
  readonly createdAt: string;
  readonly correctsNoteId: string | null;
}

export interface VisitSummary {
  readonly id: string;
  readonly reference: string;
  readonly siteId: string;
  readonly title: string | null;
  readonly categoryCode: string | null;
  readonly categoryLabel: string | null;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly timeZone: string;
  readonly scheduleMode: 'continuous' | 'daily';
  readonly planningStatus: 'draft' | 'submitted' | 'cancelled' | 'expired';
  readonly primaryHost: { readonly id: string; readonly displayName: string };
  readonly destination: { readonly id: string; readonly name: string } | null;
  readonly participantCount: number;
  readonly approvalCounts: { readonly pending: number; readonly approved: number; readonly rejected: number; readonly notRequired: number };
  readonly seriesId: string | null;
  readonly version: number;
}

export interface VisitDetailResponse extends VisitSummary {
  readonly purpose: string | null;
  /** PB-13-09: the arrival guidance a visitor of this visit receives now. */
  readonly guidance?: { readonly fields: Partial<Record<'gate' | 'parking' | 'building' | 'floor' | 'route' | 'contact', { readonly en: string; readonly ar?: string | undefined }>> };
  /** OR-10: the purpose list value, with its current labels (kept even when retired). */
  readonly purposeOption: { readonly code: string; readonly label: { readonly en: string; readonly ar?: string | undefined }; readonly active: boolean } | null;
  readonly schedule: ScheduleDto | null;
  readonly windows: ReadonlyArray<{ readonly start: string; readonly end: string }>;
  readonly additionalHosts: ReadonlyArray<{ readonly id: string; readonly displayName: string }>;
  readonly participants: readonly ParticipationResponse[];
  readonly approvals: readonly ApprovalTaskSummary[];
  readonly notes: readonly VisitNoteResponse[];
  readonly policyVersion: { readonly id: string; readonly number: number } | null;
  readonly submittedAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancelReason: string | null;
  readonly copiedFromId: string | null;
  readonly occurrenceDate: string | null;
  /** Problems that block submission of a draft (PB-04-05); empty when ready. */
  readonly submissionProblems: ReadonlyArray<{ readonly path: string; readonly message: string }>;
  /** What the caller may do next (the API still checks every command). */
  readonly can: { readonly edit: boolean; readonly cancel: boolean; readonly submit: boolean; readonly addNote: boolean; readonly copy: boolean; readonly confirmWalkIn: boolean };
  /** PB-06-03: present for visits registered at reception. */
  readonly walkIn: { readonly hostConfirmedAt: string | null } | null;
  /** PB-07-15: whether the visitor type needs an escort, and who is responsible now. */
  readonly escortRequired: boolean;
  readonly escort: { readonly id: string; readonly host: { readonly id: string; readonly displayName: string }; readonly assignedAt: string; readonly note: string | null } | null;
  readonly canAssignEscort: boolean;
  /** PB-12-03 (PD-477): acknowledged over-capacity warnings of this visit's destination. */
  readonly roomWarnings?: readonly RoomWarningDto[];
}

export interface VisitListResponse {
  readonly items: readonly VisitSummary[];
  readonly nextCursor: string | null;
}

export interface CreateVisitResponse {
  readonly visit: VisitDetailResponse;
  /** For a recurring series: every occurrence with its own outcome (one failure never fails the others). */
  readonly occurrences?: ReadonlyArray<{ readonly date: string; readonly visitId: string; readonly outcome: 'draft' | 'submitted' | 'failed'; readonly message?: string }>;
  /** PB-04-02 (PD-471): months whose date does not exist, and dates removed by exclusions. */
  readonly skipped?: readonly SkippedOccurrenceDto[];
  readonly excluded?: readonly string[];
  readonly seriesId?: string;
}

/** Returned with 409 when a visit changed since it was loaded (PB-04-06: diff and reload, not last-write-wins). */
export interface VisitConflictDetails {
  readonly currentVersion: number;
  readonly changedFields: readonly string[];
}

// ------------------------------------------------------------------ approvals (module 07)
export const ApprovalListQuery = z.object({
  siteId: Uuid,
  view: z.enum(['pending', 'decided', 'blocked']).default('pending'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const DecideApprovalRequest = z.object({
  decision: z.enum(['approve', 'reject']),
  /** The visit version the approver reviewed; a later material change makes the decision stale. */
  visitVersion: z.number().int().min(1),
  reason: optionalText(1000),
});
export const ReassignApprovalRequest = z.object({
  to: Resolver,
  reason: trimmed(500),
});

export interface ApprovalQueueItem {
  readonly task: ApprovalTaskSummary;
  readonly visit: {
    readonly id: string;
    readonly reference: string;
    readonly title: string | null;
    readonly categoryLabel: string | null;
    readonly startsAt: string | null;
    readonly endsAt: string | null;
    readonly timeZone: string;
    readonly destination: string | null;
    readonly restrictedDestination: boolean;
    readonly primaryHost: string;
    readonly purpose: string | null;
    readonly version: number;
  };
  readonly person: { readonly displayName: string; readonly organisation: string | null; readonly maskedEmail: string | null };
  /** False with a reason when the caller is conflicted (own visit) or not the named approver. */
  readonly canDecide: boolean;
  readonly blockedReason: 'own_visit' | 'not_assigned' | 'already_voted' | 'waiting' | null;
}
export interface ApprovalQueueResponse {
  readonly items: readonly ApprovalQueueItem[];
  readonly counts: { readonly pending: number; readonly blocked: number };
}

// ------------------------------------------------------------------ invitations (PB-04-08/09/15, OR-03, PD-028)
export const ReissueInvitationRequest = z.object({ mode: z.enum(['email', 'link']), reason: trimmed(500) });
export interface ReissueInvitationResponse {
  readonly mode: 'email' | 'link';
  /** Only for mode "link": shown once to the host; earlier links stop working. */
  readonly link: string | null;
  readonly expiresAt: string | null;
}
export interface InvitationSummary {
  readonly status: 'active' | 'superseded' | 'revoked';
  readonly via: 'email' | 'link';
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly revokedReason: string | null;
  readonly lastViewedAt: string | null;
}
export interface MessageSummary {
  readonly purpose: 'invitation' | 'cancellation';
  readonly status: 'queued' | 'accepted' | 'failed' | 'blocked' | 'refused' | 'unknown'; // refused/unknown: PD-584
  readonly statusReason: string | null;
  readonly at: string;
}

export const PublicInvitationRequest = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{20,200}$/, 'v.invalid') });
export const RsvpRequest = PublicInvitationRequest.extend({ response: z.enum(['confirmed', 'declined']) });

/** Invitation-scoped projection (PB-05-01, PB-05-08): only what the visitor needs, nothing about others. */
export interface VisitorPassResponse {
  readonly organisation: string;
  /** PB-13-09 (PD-060): gate, parking, building, floor, route and contact guidance for this visit. */
  readonly guidance?: { readonly fields: Partial<Record<'gate' | 'parking' | 'building' | 'floor' | 'route' | 'contact', { readonly en: string; readonly ar?: string | undefined }>> };
  readonly site: { readonly name: string; readonly address: string | null; readonly instructions: string | null; readonly timeZone: string };
  readonly branding: { readonly primaryColour: string | null; readonly contactLine: string | null };
  readonly visit: {
    readonly reference: string;
    readonly title: string | null;
    readonly startsAt: string;
    readonly endsAt: string;
    readonly scheduleMode: 'continuous' | 'daily';
    readonly dailyStart: string | null;
    readonly dailyEnd: string | null;
    readonly destination: string | null;
    readonly hostName: string;
  };
  readonly visitor: { readonly givenName: string };
  readonly rsvp: 'none' | 'confirmed' | 'declined';
  readonly preRegistration: PreRegistrationView;
  /** PB-13-05/06: message channels the visitor may opt into (only those the organisation uses). */
  readonly consent: { readonly channels: ReadonlyArray<{ readonly channel: 'sms' | 'whatsapp'; readonly granted: boolean }> };
  readonly expiresAt: string;
  /** RFC 5545 calendar entry (stable UID, SEQUENCE = visit version). */
  readonly ics: string;
  /** PB-10-04 (vehicles stream): the visitor's own current parking allocation and directions. */
  readonly parking?: readonly PassParkingDto[];
}

// ------------------------------------------------------------------ reception (module 06)
const IsoDateTime = z.iso.datetime({ offset: true });
export const BoardQuery = z.object({ siteId: Uuid, date: LocalDate.optional() });
export type ArrivalBucketDto = 'expected' | 'waiting' | 'onsite' | 'departed' | 'overdue' | 'not_arrived';

export interface BoardItem {
  readonly participationId: string;
  readonly visitId: string;
  readonly reference: string;
  readonly visitTitle: string | null;
  readonly visitor: { readonly personId: string; readonly displayName: string; readonly organisation: string | null };
  readonly host: { readonly id: string; readonly displayName: string };
  readonly destination: string | null;
  /** The authorised window that applies on the selected day. */
  readonly window: { readonly start: string; readonly end: string } | null;
  readonly timeZone: string;
  readonly bucket: ArrivalBucketDto;
  readonly flags: { readonly early: boolean; readonly late: boolean; readonly walkIn: boolean };
  readonly planningStatus: string;
  readonly approvalStatus: string;
  readonly evidenceStatus: string;
  readonly presenceStatus: string;
  readonly credentialStatus: string;
  readonly rsvpStatus: string;
  readonly waiting: { readonly id: string; readonly reason: string; readonly startedAt: string; readonly version: number } | null;
  readonly card: { readonly id: string; readonly number: string } | null;
  readonly lastPresenceAt: string | null;
}

export interface ReceptionBoardResponse {
  readonly siteId: string;
  readonly date: string;
  readonly timeZone: string;
  readonly isToday: boolean;
  /** Presence is shown as of this instant (now for today, end of day for past dates). */
  readonly asOf: string;
  readonly counts: Readonly<Record<ArrivalBucketDto, number>>;
  readonly items: readonly BoardItem[];
}

export const LookupRequest = z.object({ siteId: Uuid, query: z.string().trim().min(2, 'v.tooShort').max(300, 'v.tooLong') });

export interface ArrivalEvaluationDto {
  readonly eligible: boolean;
  readonly blockers: readonly string[];
  readonly warnings: readonly string[];
  readonly window: { readonly start: string; readonly end: string } | null;
  readonly token: string;
  /** PB-02-04: when the facts were read and the policy version they were judged under. */
  readonly evaluatedAt?: string;
  readonly policyVersion?: number | null;
}

export interface ReceptionDetailResponse {
  readonly item: BoardItem;
  readonly evaluation: ArrivalEvaluationDto;
  readonly presence: ReadonlyArray<{ readonly kind: 'arrival' | 'departure'; readonly observedAt: string; readonly recordedAt: string; readonly recordedBy: string | null; /** OR-16: the desk it was recorded at. */ readonly desk?: string | null }>;
  readonly waitingHistory: ReadonlyArray<{ readonly id: string; readonly reason: string; readonly startedAt: string; readonly resolvedAt: string | null; readonly resolution: string | null; readonly reasonChanges: number }>;
  readonly badge: { readonly badgeId: string; readonly attempts: number; readonly lastStatus: string } | null;
  readonly participationVersion: number;
}

export const ArrivalRequest = z.object({
  evaluationToken: z.string().min(10).max(64),
  observedAt: IsoDateTime.optional(),
  note: z.string().trim().max(500).optional(),
  /** PB-07-15: the operator confirms the named escort is present (required when the type needs one). */
  escortConfirmed: z.boolean().optional(),
});
export const DepartureRequest = z.object({ observedAt: IsoDateTime.optional(), card: z.enum(['returned', 'lost', 'not_returned']).optional(), note: z.string().trim().max(500).optional() });
export const WAIT_REASONS = ['host', 'approval', 'escort', 'evidence', 'other'] as const;
export const StartWaitingRequest = z.object({ reason: z.enum(WAIT_REASONS), note: z.string().trim().max(500).optional() });
export const UpdateWaitingRequest = z.object({
  expectedVersion: z.number().int().min(1),
  reason: z.enum(WAIT_REASONS).optional(),
  note: z.string().trim().max(500).optional(),
  resolve: z.enum(['left', 'cancelled', 'other']).optional(),
});
export const AssignCardRequest = z.object({ cardId: Uuid });
export const CardLostRequest = z.object({ reason: trimmed(500) });
export const CreateCardRequest = z.object({ number: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/, 'v.cardNumber'), label: optionalText(80) });
export const UpdateCardRequest = z.object({ expectedVersion: z.number().int().min(1), custody: z.enum(['available', 'disabled', 'maintenance']).optional(), label: optionalText(80) });
export interface CardResponse {
  readonly id: string;
  readonly siteId: string;
  readonly number: string;
  readonly label: string | null;
  readonly custody: 'available' | 'assigned' | 'returned_held' | 'lost' | 'disabled' | 'maintenance';
  readonly version: number;
  readonly assignedTo: { readonly participationId: string; readonly visitor: string; readonly since: string } | null;
}

export const BadgeRequest = z.object({
  reason: optionalText(500),
  printer: z.string().trim().min(1).max(80).default('browser'),
  /** PB-06-09 (PD-513): send the badge to this edge printer in the same step (desk printer). */
  peripheralId: Uuid.optional(),
});
export interface BadgeResponse {
  readonly printJobId: string;
  readonly badgeId: string;
  readonly attempt: number;
  readonly templateVersion: string;
  /** PB-11-01 published badge template layout (credentials stream); absent from older servers. */
  readonly layout?: { readonly size: 'cr80' | 'a6'; readonly fields: readonly string[]; readonly accentColour: string | null; readonly statusBand: boolean };
  /** Only approved fields (PB-06-09). */
  readonly fields: {
    readonly visitorName: string;
    readonly organisation: string | null;
    readonly hostName: string;
    readonly siteName: string;
    readonly destination: string | null;
    readonly validUntil: string;
    readonly timeZone: string;
    readonly reference: string;
    readonly category: string | null;
    readonly brandColour: string | null;
  };
  /** PB-06-09 (PD-513): the device job when the badge was sent to an edge printer. */
  readonly deviceJob?: DeviceJobView | null;
}
export const PrintOutcomeRequest = z.object({ status: z.enum(['printed', 'failed', 'unknown']) });

export const WalkInRequest = z.object({
  siteId: Uuid,
  hostId: Uuid,
  categoryCode: z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey'),
  purpose: optionalText(500),
  destinationId: Uuid.nullable().optional(),
  /** Local end time today in the site zone; defaults to the end of the configured day. */
  until: LocalTime.optional(),
  participant: ParticipantInput,
});

export interface ReceptionTaskDto {
  readonly id: string;
  readonly kind: 'waiting' | 'card_missing' | 'overstay' | 'print_unknown' | 'departure_unconfirmed' | 'other' | 'kiosk_assistance' | 'kiosk_walk_in';
  readonly participationId: string | null;
  readonly visitId: string | null;
  readonly summary: string | null;
  readonly createdAt: string;
  readonly visitor: string | null;
}
export interface ReceptionTasksResponse {
  readonly items: readonly ReceptionTaskDto[];
  readonly lastHandover: { readonly by: string; readonly at: string; readonly note: string | null } | null;
}
export const ResolveTaskRequest = z.object({ resolution: trimmed(500) });
export const HandoverRequest = z.object({ siteId: Uuid, note: optionalText(1000) });

// ------------------------------------------------------------------ pre-registration (module 05, PD-035)
/** PB-05-07 (PD-451): a document whose validity ends before the visit's last local day. */
export interface ExpiredDocumentDto {
  readonly key: string;
  readonly label: { readonly en: string; readonly ar?: string | undefined };
  readonly validUntil: string;
  readonly required: boolean;
}
/** Host-facing readiness on the visit detail (PB-05-07). */
export interface ParticipantReadiness {
  readonly complete: boolean;
  readonly outstanding: number;
  /** The visit's last local day documents must be valid through. */
  readonly requiredThrough?: string | null;
  readonly expired?: readonly ExpiredDocumentDto[];
  /** Latest recorded readiness regression and the state of the host notice about it (PD-452). */
  readonly regression?: { readonly detectedAt: string; readonly hostNotice: 'queued' | 'accepted' | 'failed' | 'blocked' | 'refused' | 'unknown' | null } | null;
}
export interface PreRegistrationView {
  readonly complete: boolean;
  readonly requiredBeforeArrival: boolean;
  /** PB-05-04 (PD-510): the site's time zone; receipt times are shown in it. */
  readonly timeZone?: string;
  /** PB-05-07: documents must be valid through this local date (the visit's last day). */
  readonly requiredThrough?: string | null;
  readonly contact: {
    readonly required: boolean;
    readonly confirmed: boolean;
    readonly editable: readonly ('email' | 'phone' | 'organisation' | 'name')[];
    /** Email and phone are masked: a pass link can be forwarded. */
    readonly values: { readonly givenName: string; readonly familyName: string | null; readonly email: string | null; readonly phone: string | null; readonly organisation: string | null };
  };
  readonly notices: ReadonlyArray<{
    readonly key: string;
    /** Published version id to accept; null when the organisation has not published this notice. */
    readonly noticeId: string | null;
    readonly kind: 'acknowledgement' | 'signature';
    readonly number: number | null;
    readonly title: string | null;
    readonly body: string | null;
    readonly purpose: string | null;
    readonly status: string;
    /** PB-05-04 (PD-510): the latest recorded decision about this notice, as a receipt. */
    readonly receipt?: NoticeReceiptDto | null;
    /** PB-08-13 (PD-511): optional UAE PASS signing of that receipt; null when not offered. */
    readonly signing?: NoticeSigningOption | null;
  }>;
  readonly documents: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly required: boolean;
    readonly status: string;
    /** PB-05-07: recorded validity (visitor-stated or reviewer-confirmed); null = none recorded. */
    readonly validUntil?: string | null;
    /** The upload must state a validity date (PD-450). */
    readonly validUntilRequired?: boolean;
    /**
     * PD-707 (clamav stream): malware-check state of the current upload, for a neutral message —
     * 'rejected' (the file was rejected by the malware check) or 'not_checked' (could not be checked
     * yet). Absent when the check passed, no scanner is configured, or nothing was uploaded.
     */
    readonly malwareCheck?: 'rejected' | 'not_checked';
  }>;
}

export const ContactConfirmRequest = PublicInvitationRequest.extend({
  givenName: z.string().trim().min(1, 'v.required').max(100, 'v.tooLong').optional(),
  familyName: optionalText(100),
  email: z.email('v.email').max(254, 'v.tooLong').nullable().optional(),
  phone: z.string().trim().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').nullable().optional(),
  organisation: optionalText(200),
});
export const AcknowledgeRequest = PublicInvitationRequest.extend({
  noticeId: Uuid,
  decision: z.enum(['accepted', 'declined']),
  signedName: z.string().trim().max(200, 'v.tooLong').optional(),
});

export const NoticeListQuery = z.object({ siteId: Uuid.optional() });
export const CreateNoticeRequest = z.object({
  siteId: Uuid.nullable(),
  key: Key,
  kind: z.enum(['acknowledgement', 'signature']),
  purpose: optionalText(300),
  title: Label,
  body: z.object({ en: trimmed(20000), ar: z.string().trim().max(20000, 'v.tooLong').optional() }),
  requiresReacceptance: z.boolean().default(true),
});
export interface NoticeResponse {
  readonly id: string;
  readonly siteId: string | null;
  readonly key: string;
  readonly kind: 'acknowledgement' | 'signature';
  readonly purpose: string | null;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'retired';
  readonly title: { readonly en: string; readonly ar?: string | undefined };
  readonly body: { readonly en: string; readonly ar?: string | undefined };
  readonly requiresReacceptance: boolean;
  readonly publishedAt: string | null;
}

// ------------------------------------------------------------------ site override removal (PB-12-05, PD-062)
export const RemovePolicyOverrideRequest = z.object({ expectedNumber: z.number().int().min(1), reason: z.string().trim().min(1, 'v.required').max(500, 'v.tooLong') });
export interface PolicyOverridePreviewResponse {
  readonly siteId: string;
  /** The site's own published policy, if any. */
  readonly override: { readonly id: string; readonly number: number } | null;
  /** The shared policy that is (or would become) effective at the site. */
  readonly shared: { readonly id: string; readonly number: number };
  readonly diff: {
    readonly categoriesAdded: readonly string[];
    readonly categoriesRemoved: readonly string[];
    readonly categoriesChanged: ReadonlyArray<{ readonly code: string; readonly fields: readonly string[] }>;
    readonly sectionsChanged: readonly string[];
  } | null;
}
